import { useEffect, useRef } from 'react';
import type { KeyboardEvent } from 'react';

import { CheckIcon } from '../../icons/CheckIcon';
import { CloseIcon } from '../../icons/CloseIcon';
import {
  AppliedAmount,
  AppliedBody,
  AppliedCard,
  AppliedCode,
  AppliedDescription,
  AppliedList,
  ApplyButton,
  ErrorRow,
  Field,
  FieldStack,
  InlineLabel,
  Input,
  RemoveIconButton,
  SuccessCheck,
  SuccessRow,
  Wrapper,
} from './DiscountCode.styles';
import type { DiscountCodeProps } from './DiscountCode.types';

export function DiscountCode({
  kind = 'promo',
  applied,
  successMessage,
  value,
  onChange,
  onApply,
  onRemove,
  placeholder,
  applyLabel,
  removeLabel,
  error,
  disabled,
  className,
}: DiscountCodeProps) {
  const appliedList = applied ?? [];
  const canApply = !disabled && value.trim().length > 0;
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const pendingFocusRef = useRef<{ appliedCount: number } | null>(null);

  useEffect(() => {
    const pending = pendingFocusRef.current;
    if (!pending) return;
    const countChanged = appliedList.length !== pending.appliedCount;
    if (disabled && (!countChanged || appliedList.length === 0)) return;
    pendingFocusRef.current = null;

    const active = document.activeElement;
    if (active && active !== document.body) return;

    const removeButtons =
      wrapperRef.current?.querySelectorAll<HTMLButtonElement>('[data-discount-remove]') ?? [];
    const lastRemove = removeButtons[removeButtons.length - 1];
    if (appliedList.length > pending.appliedCount && lastRemove) {
      lastRemove.focus();
    } else if (!disabled) {
      inputRef.current?.focus();
    } else {
      lastRemove?.focus();
    }
  }, [appliedList.length, disabled, error]);

  const trackFocus = () => {
    pendingFocusRef.current = { appliedCount: appliedList.length };
  };

  const handleApply = () => {
    trackFocus();
    onApply();
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter' && canApply) {
      e.preventDefault();
      handleApply();
    }
  };

  return (
    <Wrapper ref={wrapperRef} className={className}>
      <Field data-error={error ? true : undefined} data-disabled={disabled || undefined}>
        <FieldStack>
          {value && placeholder && <InlineLabel>{placeholder}</InlineLabel>}
          <Input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value.toUpperCase())}
            onKeyDown={handleKeyDown}
            placeholder={value ? undefined : placeholder}
            disabled={disabled}
            aria-label={placeholder}
            aria-invalid={Boolean(error)}
          />
        </FieldStack>
        <ApplyButton
          type="button"
          onClick={handleApply}
          disabled={!canApply}
          data-active={canApply || undefined}
        >
          {applyLabel}
        </ApplyButton>
      </Field>

      {error && (
        <ErrorRow role="alert">
          <CloseIcon size={12} />
          <span>{error}</span>
        </ErrorRow>
      )}

      {successMessage && appliedList.length > 0 && (
        <SuccessRow>
          <SuccessCheck aria-hidden="true">
            <CheckIcon size={12} />
          </SuccessCheck>
          <span>{successMessage}</span>
        </SuccessRow>
      )}

      {appliedList.length > 0 && (
        <AppliedList>
          {appliedList.map(({ code, amount, description }) => {
            const showAmount = amount !== undefined;
            const showDescription = kind === 'voucher' && description !== undefined;
            return (
              <AppliedCard key={code}>
                <AppliedBody>
                  <AppliedCode>{code}</AppliedCode>
                  {showDescription && <AppliedDescription>{description}</AppliedDescription>}
                </AppliedBody>
                {showAmount && <AppliedAmount>{amount}</AppliedAmount>}
                {onRemove && (
                  <RemoveIconButton
                    type="button"
                    onClick={() => {
                      trackFocus();
                      onRemove(code);
                    }}
                    aria-label={`${removeLabel} ${code}`}
                    data-discount-remove
                  >
                    <CloseIcon size={12} />
                  </RemoveIconButton>
                )}
              </AppliedCard>
            );
          })}
        </AppliedList>
      )}
    </Wrapper>
  );
}
